import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import logo from '../assets/Header-blue.svg';
import api from '../utils/api';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('This reset link is invalid or has expired.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not reset your password. The link may have expired.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-screen max-h-screen w-full flex flex-col md:flex-row bg-white overflow-hidden">
      {/* Left Panel - Form Area */}
      <div className="w-full md:w-1/2 h-screen max-h-screen flex flex-col p-6 md:p-8 lg:p-10 bg-white overflow-hidden shrink-0">
        <div className="mb-4 shrink-0">
          <img src={logo} alt="C.A.R. Dispute Resolution Center" className="h-12 object-contain" />
        </div>

        <div className="flex-grow min-h-0 flex flex-col justify-center max-w-md w-full mx-auto py-4">
          <Link
            to="/login"
            className="group mb-8 flex items-center text-[#5b5a64] hover:text-[#0e1e20] transition-colors"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mr-2 group-hover:-translate-x-1 transition-transform">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            <span className="text-sm font-medium">Back to Sign In</span>
          </Link>

          {!done ? (
            <>
              <div className="mb-8">
                <h1 className="text-[30px] font-bold text-[#0e1e20] mb-2" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
                  Set a New Password
                </h1>
                <p className="text-[#5b5a64] text-base" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Choose a new password for your account.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-[#0e1e20] mb-1.5" style={{ fontFamily: "'Inter', sans-serif" }}>
                    New Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-[#e5e5e5] text-sm text-[#0e1e20] placeholder-[#9ca3af] outline-none transition-all focus:border-[#115e59] focus:ring-2 focus:ring-[#115e59]/20"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  />
                </div>

                <div>
                  <label htmlFor="confirm" className="block text-sm font-medium text-[#0e1e20] mb-1.5" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Confirm Password
                  </label>
                  <input
                    id="confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-enter your new password"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-[#e5e5e5] text-sm text-[#0e1e20] placeholder-[#9ca3af] outline-none transition-all focus:border-[#115e59] focus:ring-2 focus:ring-[#115e59]/20"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  />
                </div>

                {error && (
                  <p className="text-sm text-[#b91c1c]" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#115e59] text-white py-3 rounded-lg font-medium hover:bg-[#0f766e] active:bg-[#134e4a] transition-colors text-base disabled:opacity-60"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {submitting ? 'Updating…' : 'Update Password'}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-[#d1fae5] rounded-full flex items-center justify-center mx-auto mb-5">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#065f46" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-[#0e1e20] mb-2" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
                Password Updated
              </h2>
              <p className="text-[#5b5a64] text-base mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
                Your password has been changed. Redirecting you to sign in…
              </p>
              <Link to="/login" className="text-[#115e59] font-medium hover:text-[#0f766e] transition-colors" style={{ fontFamily: "'Inter', sans-serif" }}>
                Go to Sign In
              </Link>
            </div>
          )}
        </div>

        <div className="mt-4 text-xs text-[#5b5a64] shrink-0" style={{ fontFamily: "'Inter', sans-serif" }}>
          &copy; 2026 Scale Mediation
        </div>
      </div>

      {/* Right Panel - Teal Visual Area */}
      <div className="hidden md:flex w-1/2 h-screen max-h-screen relative overflow-hidden flex-col items-center justify-center p-6 lg:p-8 text-center shrink-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#115e59] via-[#0d5450] to-[#0d4f4a]" />
        <div className="relative z-20 max-w-lg mx-auto">
          <h2 className="text-2xl font-bold text-white mb-3 tracking-tight" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
            Secure Access, Always
          </h2>
          <p className="text-white/80 text-base leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            Reset links are single-use and expire quickly. Once you set a new password, any other active sessions are signed out.
          </p>
        </div>
      </div>
    </div>
  );
}
